<script setup lang="ts">
  import {
    type SuperUserOrganisationCreateRequest,
    type StripeCustomersListResponse,
    type LawFields,
    type SuperUserHubSpotCompany,
    type HubspotCompaniesListResponse
  } from '@/types/generated/types.gen'
  import {
    OrganisationsService,
    StripeCustomersService,
    LawFieldsService,
    HubspotService
  } from '@/types/generated/services.gen'
  import { ref, onMounted, onUnmounted, computed, reactive, watch } from 'vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import MultiSelectField from '@/components/admin/form/MultiSelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import DatePickerField from '@/components/admin/form/DatePickerField.vue'
  import router from '@/router'
  import { Form, type GenericObject } from 'vee-validate'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import FormGrid from '@/components/admin/form/FormGrid.vue'
  import { yup } from '@/lib/validation'
  import { useTranslation } from '@/composables/useTranslation'
  import { useLawFieldTranslation } from '@/composables/useLawFieldTranslation'
  import { createSuccessToast, genericErrorToast } from '@/lib/toasts'
  import { useToast } from '@/components/ui/toast'
  import { type Option as SelectOption } from '@/components/admin/form/SelectField.vue'
  import { type Option as MultiSelectOption } from '@/components/admin/form/MultiSelectField.vue'
  import {
    Plus,
    Trash2,
    ArrowLeft,
    Search,
    Building2,
    ExternalLink,
    CreditCard,
    Pencil
  } from 'lucide-vue-next'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'

  const { toast } = useToast()

  const { t } = useTranslation({
    en: {
      newCustomer: 'New Customer',
      backToList: 'Back to customers list',
      customerInformation: 'Customer Information',
      customerName: 'Customer Name',
      primaryContact: 'Primary Contact',
      phone: 'Phone',
      email: 'Email',
      street: 'Street',
      house: 'House',
      postcode: 'Postcode',
      city: 'City',
      country: 'Country',
      billingInformation: 'Billing Information',
      connectToStripe: 'Connect to an existing customer profile in Stripe',
      stripeCustomerId: 'Stripe Customer ID',
      assignUsers: 'Assign Users',
      userFullName: 'User full name',
      userEmail: 'User email',
      role: 'Role',
      addUser: 'Add User',
      selectedOnboardingTime: 'Selected Onboarding Time',
      onboardingDate: 'Onboarding Date',
      onboardingTime: 'Onboarding Time',
      accountSettings: 'Account Settings',
      lawFields: 'Law Fields',
      selectLawFields: 'Select several law fields',
      seats: 'Seats',
      appointmentBookingSeats: 'Appointment booking seats',
      hasChurned: 'Has Churned',
      monsumCustomerNumber: 'Monsum Customer Number',
      isTestAccount: 'Test Account',
      inboundEmailSlug: 'JUPUS Email',
      enableInboundEmailReplies: 'Enable Inbound Email Replies',
      canConfigureEmail: 'Can Configure Email Server',
      isNewUiSwitchEnabled: 'Enable New UI for Testing (can switch freely)',
      isForcedToUseNewUi: 'Enable New UI by Default (cannot switch)',
      uiSettings: 'UI Settings',
      features: 'Features',
      activateAI: 'Activate AI',
      activateCases: 'Activate Cases',
      activateAIAnsweringMachine: 'Activate AI Answering Machine',
      emailResponseFromClients: 'Email Response From Clients',
      appointmentBooking: 'Appointment Booking',
      enableNewUiSwitch: 'Enable New UI Switch (can switch between old and new)',
      forceUseOfNewUi: 'Force Use of New UI (can not switch back)',
      advancedSettings: 'Advanced Settings',
      aiWorkflows: 'AI Workflows',
      phonebotLanguage: 'Phonebot Language',
      legalJurisdiction: 'Legal Jurisdiction',
      back: 'Back',
      finishAndSendInvites: 'Finish and Send Invites',
      attention: 'Attention',
      attentionText:
        'Creating a customer will register the organization and send invitation emails to all assigned users.',
      admin: 'Admin',
      lawyer: 'Lawyer',
      clerk: 'Clerk',
      firstName: 'First Name',
      lastName: 'Last Name',
      importFromHubspot: 'Import from HubSpot',
      hubspotImportTitle: 'Import from HubSpot',
      hubspotImportDescription: 'Search for a company in HubSpot to import its data',
      searchHubspotCompanies: 'Search companies...',
      noHubspotResults: 'No companies found',
      loadingHubspot: 'Loading...',
      selectCompany: 'Select',
      hubspotId: 'HubSpot ID',
      domain: 'Domain',
      createStripeCustomer: 'Create Stripe Customer',
      stripeCreateTitle: 'Create New Stripe Customer',
      stripeCreateDescription: 'Create a new customer in Stripe with billing information',
      createCustomer: 'Create Customer',
      creating: 'Creating...',
      orCreateNew: 'or create a new one',
      selectOnboardingTime: 'Select onboarding time',
      editOnboardingTime: 'Edit Onboarding Time',
      save: 'Save',
      cancel: 'Cancel',
      // Validation messages
      validation: {
        customerNameRequired: 'Customer name is required',
        customerNameMinLength: 'Name must be at least 2 characters',
        emailRequired: 'Email is required',
        emailInvalid: 'Please enter a valid email address',
        emailMaxLength: 'Email must not exceed 320 characters',
        phoneInvalid: 'Please enter a valid phone number',
        streetRequired: 'Street is required',
        houseRequired: 'House number is required',
        postcodeRequired: 'Postcode is required',
        postcodeFormat: 'Postcode must be 5 digits for Germany',
        cityRequired: 'City is required',
        countryRequired: 'Country is required',
        seatsRequired: 'Number of seats is required',
        seatsMinimum: 'Must have at least 1 seat',
        seatsInteger: 'Must be a whole number',
        appointmentSeatsRequired: 'Appointment seats is required',
        appointmentSeatsMinimum: 'Cannot be negative',
        appointmentSeatsInteger: 'Must be a whole number',
        firstNameRequired: 'First name is required',
        firstNameMinLength: 'First name must be at least 2 characters',
        lastNameRequired: 'Last name is required',
        lastNameMinLength: 'Last name must be at least 2 characters',
        userEmailRequired: 'Email is required',
        userEmailInvalid: 'Please enter a valid email address',
        stripeNameRequired: 'Customer name is required (minimum 2 characters)',
        stripeEmailRequired: 'Email is required',
        stripeEmailInvalid: 'Please enter a valid email address'
      },
      // Error messages
      errors: {
        validationError: 'Validation Error',
        atLeastOneUserRequired: 'At least one user must be added',
        fixUserInformationErrors: 'Please fix user information errors',
        duplicateEmailFound: 'Duplicate email address found',
        stripeCustomerCreationFailed: 'Failed to create Stripe customer. Please try again.',
        customerCreationFailed:
          'Failed to create customer. Please check your information and try again.',
        stripeValidationFailed: 'Please check your Stripe customer information'
      }
    },
    de: {
      newCustomer: 'Neuer Kunde',
      backToList: 'Zurück zur Kundenliste',
      customerInformation: 'Kundeninformationen',
      customerName: 'Kundenname',
      primaryContact: 'Hauptansprechpartner',
      phone: 'Telefon',
      email: 'E-Mail',
      street: 'Straße',
      house: 'Haus',
      postcode: 'PLZ',
      city: 'Stadt',
      country: 'Land',
      billingInformation: 'Rechnungsinformationen',
      connectToStripe: 'Mit einem bestehenden Kundenprofil in Stripe verbinden',
      stripeCustomerId: 'Stripe Kunden-ID',
      assignUsers: 'Benutzer zuweisen',
      userFullName: 'Vollständiger Name',
      userEmail: 'E-Mail',
      role: 'Rolle',
      addUser: 'Benutzer hinzufügen',
      selectedOnboardingTime: 'Gewählter Onboarding-Zeitpunkt',
      onboardingDate: 'Onboarding Datum',
      onboardingTime: 'Onboarding Uhrzeit',
      accountSettings: 'Kontoeinstellungen',
      lawFields: 'Rechtsgebiete',
      selectLawFields: 'Mehrere Rechtsgebiete auswählen',
      seats: 'Plätze',
      appointmentBookingSeats: 'Terminbuchungsplätze',
      hasChurned: 'Hat gekündigt',
      monsumCustomerNumber: 'Monsum Kundennummer',
      isTestAccount: 'Testkanzlei',
      inboundEmailSlug: 'JUPUS Email',
      enableInboundEmailReplies: 'Eingehende E-Mail-Antworten aktivieren',
      canConfigureEmail: 'Eigenen E-Mail-Server konfigurieren',
      isNewUiSwitchEnabled: 'Neues UI zum Ausprobieren aktivieren (kann beliebig wechseln)',
      isForcedToUseNewUi: 'Neues UI standardmäßig aktivieren (kann nicht wechseln)',
      uiSettings: 'UI Einstellungen',
      features: 'Features',
      activateAI: 'KI aktivieren',
      activateCases: 'Anfragen aktivieren',
      activateAIAnsweringMachine: 'KI-Anrufbeantworter aktivieren',
      emailResponseFromClients: 'E-Mail Antwort von Mandanten',
      appointmentBooking: 'Terminbuchung',
      enableNewUiSwitch: 'Neues UI zum Ausprobieren aktivieren (kann beliebig wechseln)',
      forceUseOfNewUi: 'Neues UI erzwingen (kann nicht zurück wechseln)',
      advancedSettings: 'Erweiterte Einstellungen',
      aiWorkflows: 'KI-Workflows',
      phonebotLanguage: 'Phonebot Sprache',
      legalJurisdiction: 'Rechtsordnung',
      back: 'Zurück',
      finishAndSendInvites: 'Abschließen und Einladungen senden',
      attention: 'Achtung',
      attentionText:
        'Das Erstellen eines Kunden registriert die Organisation und sendet Einladungs-E-Mails an alle zugewiesenen Benutzer.',
      admin: 'Admin',
      lawyer: 'Anwalt',
      clerk: 'Sachbearbeiter',
      firstName: 'Vorname',
      lastName: 'Nachname',
      importFromHubspot: 'Aus HubSpot importieren',
      hubspotImportTitle: 'Aus HubSpot importieren',
      hubspotImportDescription:
        'Suchen Sie nach einem Unternehmen in HubSpot, um dessen Daten zu importieren',
      searchHubspotCompanies: 'Unternehmen suchen...',
      noHubspotResults: 'Keine Unternehmen gefunden',
      loadingHubspot: 'Laden...',
      selectCompany: 'Auswählen',
      hubspotId: 'HubSpot ID',
      domain: 'Domain',
      createStripeCustomer: 'Stripe Kunde erstellen',
      stripeCreateTitle: 'Neuen Stripe Kunden erstellen',
      stripeCreateDescription:
        'Erstellen Sie einen neuen Kunden in Stripe mit Rechnungsinformationen',
      createCustomer: 'Kunde erstellen',
      creating: 'Erstellen...',
      orCreateNew: 'oder erstellen Sie einen neuen',
      selectOnboardingTime: 'Wählen Sie eine Onboarding-Zeit aus',
      editOnboardingTime: 'Onboarding-Zeit bearbeiten',
      save: 'Speichern',
      cancel: 'Abbrechen',
      // Validation messages
      validation: {
        customerNameRequired: 'Kundenname ist erforderlich',
        customerNameMinLength: 'Name muss mindestens 2 Zeichen lang sein',
        emailRequired: 'E-Mail ist erforderlich',
        emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
        emailMaxLength: 'E-Mail darf 320 Zeichen nicht überschreiten',
        phoneInvalid: 'Bitte geben Sie eine gültige Telefonnummer ein',
        streetRequired: 'Straße ist erforderlich',
        houseRequired: 'Hausnummer ist erforderlich',
        postcodeRequired: 'Postleitzahl ist erforderlich',
        postcodeFormat: 'Postleitzahl muss 5 Ziffern für Deutschland haben',
        cityRequired: 'Stadt ist erforderlich',
        countryRequired: 'Land ist erforderlich',
        seatsRequired: 'Anzahl der Plätze ist erforderlich',
        seatsMinimum: 'Muss mindestens 1 Platz haben',
        seatsInteger: 'Muss eine ganze Zahl sein',
        appointmentSeatsRequired: 'Terminbuchungsplätze sind erforderlich',
        appointmentSeatsMinimum: 'Kann nicht negativ sein',
        appointmentSeatsInteger: 'Muss eine ganze Zahl sein',
        firstNameRequired: 'Vorname ist erforderlich',
        firstNameMinLength: 'Vorname muss mindestens 2 Zeichen lang sein',
        lastNameRequired: 'Nachname ist erforderlich',
        lastNameMinLength: 'Nachname muss mindestens 2 Zeichen lang sein',
        userEmailRequired: 'E-Mail ist erforderlich',
        userEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
        stripeNameRequired: 'Kundenname ist erforderlich (mindestens 2 Zeichen)',
        stripeEmailRequired: 'E-Mail ist erforderlich',
        stripeEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
      },
      // Error messages
      errors: {
        validationError: 'Validierungsfehler',
        atLeastOneUserRequired: 'Es muss mindestens ein Benutzer hinzugefügt werden',
        fixUserInformationErrors: 'Bitte beheben Sie die Fehler in den Benutzerinformationen',
        duplicateEmailFound: 'Doppelte E-Mail-Adresse gefunden',
        stripeCustomerCreationFailed:
          'Stripe-Kunde konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        customerCreationFailed:
          'Kunde konnte nicht erstellt werden. Bitte überprüfen Sie Ihre Angaben und versuchen Sie es erneut.',
        stripeValidationFailed: 'Bitte überprüfen Sie Ihre Stripe-Kundeninformationen'
      }
    }
  })

  // Validation schema
  const validationSchema = yup.object({
    name: yup
      .string()
      .required(t('validation.customerNameRequired'))
      .min(2, t('validation.customerNameMinLength')),
    email: yup
      .string()
      .required(t('validation.emailRequired'))
      .email(t('validation.emailInvalid'))
      .max(320, t('validation.emailMaxLength')),
    phone: yup.string().nullable().phone(t('validation.phoneInvalid')),
    street: yup.string().required(t('validation.streetRequired')),
    street_number: yup.string().required(t('validation.houseRequired')),
    zip: yup
      .string()
      .required(t('validation.postcodeRequired'))
      .matches(/^\d{5}$/, t('validation.postcodeFormat')),
    city: yup.string().required(t('validation.cityRequired')),
    country: yup.string().required(t('validation.countryRequired')),
    seats: yup
      .number()
      .required(t('validation.seatsRequired'))
      .min(1, t('validation.seatsMinimum'))
      .integer(t('validation.seatsInteger')),
    appointments_seats: yup
      .number()
      .required(t('validation.appointmentSeatsRequired'))
      .min(0, t('validation.appointmentSeatsMinimum'))
      .integer(t('validation.appointmentSeatsInteger'))
  })

  // User validation errors
  const userValidationErrors = ref<
    Record<number, { first_name?: string; last_name?: string; email?: string }>
  >({})

  // Form data
  const formData = reactive({
    name: '',
    primary_contact: '',
    phone: '',
    email: '',
    street: '',
    street_number: '',
    zip: '',
    city: '',
    country: 'Germany',
    stripe_customer_uuid: null as string | null,
    onboarding_start_date: null as string | null,
    onboarding_start_time: '09:30' as string,
    law_fields: [] as string[],
    seats: 5,
    appointments_seats: 2,
    has_churned: false,
    monsum_customer_number: '',
    is_test: false,
    inbound_email_slug: '',
    enable_inbound_email_replies: false,
    can_configure_email: false,
    is_new_ui_switch_enabled: false,
    is_forced_to_use_new_ui: false,
    // Feature flags
    enable_cases: false,
    enable_voice: false,
    enable_law_specific_data: false,
    ai_enabled: false,
    enable_appointments: false,
    enable_ai_workflows: false,
    default_language: 'de' as 'de' | 'fr' | 'en',
    legal_jurisdiction: 'DE' as 'DE' | 'FR'
  })

  // Users management
  interface UserEntry {
    id: number
    first_name: string
    last_name: string
    email: string
    role: 'ADMIN' | 'LAWYER' | 'CLERK' | 'PHONE_AI_USER'
  }

  const users = ref<UserEntry[]>([])
  let userIdCounter = 0

  const validateUser = (user: UserEntry): boolean => {
    const errors: { first_name?: string; last_name?: string; email?: string } = {}
    let isValid = true

    if (!user.first_name || user.first_name.trim().length < 2) {
      errors.first_name = t('validation.firstNameMinLength')
      isValid = false
    }

    if (!user.last_name || user.last_name.trim().length < 2) {
      errors.last_name = t('validation.lastNameMinLength')
      isValid = false
    }

    if (!user.email || user.email.trim().length === 0) {
      errors.email = t('validation.userEmailRequired')
      isValid = false
    } else {
      // Simple email validation
      const emailRegex = /^.+@.+\.[a-z]{2,}$/i
      if (!emailRegex.test(user.email)) {
        errors.email = t('validation.userEmailInvalid')
        isValid = false
      }
    }

    userValidationErrors.value[user.id] = errors
    return isValid
  }

  const validateAllUsers = (): boolean => {
    if (users.value.length === 0) {
      return false
    }

    let allValid = true
    users.value.forEach((user) => {
      if (!validateUser(user)) {
        allValid = false
      }
    })
    return allValid
  }

  const addUser = () => {
    users.value.push({
      id: userIdCounter++,
      first_name: '',
      last_name: '',
      email: '',
      role: 'ADMIN'
    })
  }

  const removeUser = (id: number) => {
    users.value = users.value.filter((u) => u.id !== id)
    delete userValidationErrors.value[id]
  }

  const roleOptions: MultiSelectOption[] = [
    { value: 'ADMIN', title: t('admin') },
    { value: 'LAWYER', title: t('lawyer') },
    { value: 'CLERK', title: t('clerk') },
    { value: 'PHONE_AI_USER', title: 'Phone AI user' }
  ]

  // Language options for phonebot
  const languageOptions: SelectOption[] = [
    { value: 'de', title: 'Deutsch' },
    { value: 'fr', title: 'Französisch' },
    { value: 'en', title: 'Englisch' }
  ]

  const jurisdictionOptions: SelectOption[] = [
    { value: 'DE', title: 'Deutschland' },
    { value: 'FR', title: 'Frankreich' }
  ]

  // Stripe customers
  const stripeCustomersList = ref<StripeCustomersListResponse>()
  const stripeCustomersOptions = computed<SelectOption[]>(() => {
    return (
      stripeCustomersList.value?.results.map((customer) => ({
        value: customer.uuid,
        title: (customer.name ?? '') + ' - ' + (customer.email ?? '')
      })) ?? []
    )
  })

  const { lawFieldTranslations } = useLawFieldTranslation()

  // Law fields
  const lawFields = ref<LawFields>()
  const lawFieldOptions = computed<MultiSelectOption[]>(() => {
    const translations = lawFieldTranslations.value
    return (
      Object.keys(lawFields.value?.data ?? {}).map((key) => ({
        value: key,
        title: translations[key] || key
      })) ?? []
    )
  })

  // Document AI enables both enable_law_specific_data and ai_enabled
  const documentAiEnabled = computed({
    get: () => formData.enable_law_specific_data && formData.ai_enabled,
    set: (value: boolean) => {
      formData.enable_law_specific_data = value
      formData.ai_enabled = value
    }
  })

  // Computed property for displaying onboarding date and time
  const onboardingDisplay = computed(() => {
    if (!formData.onboarding_start_date) {
      return ''
    }

    // Handle both ISO string and date input format (YYYY-MM-DD)
    const dateValue = formData.onboarding_start_date
    let date: Date

    if (typeof dateValue === 'string') {
      if (dateValue.includes('T')) {
        // ISO format
        date = new Date(dateValue)
      } else {
        // Date input format (YYYY-MM-DD) - parse as local date
        const [year, month, day] = dateValue.split('-').map(Number)
        date = new Date(year, month - 1, day)
      }
    } else {
      date = dateValue
    }

    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    const timeStr = formData.onboarding_start_time || '09:30'

    return `${dateStr}, from ${timeStr} to ${calculateEndTime(timeStr)}`
  })

  const calculateEndTime = (startTime: string) => {
    const [hours, minutes] = startTime.split(':')
    const endHours = (parseInt(hours) + 1) % 24
    return `${String(endHours).padStart(2, '0')}:${minutes}`
  }

  const showOnboardingModal = ref(false)

  // HubSpot modal state
  const showHubspotModal = ref(false)
  const hubspotSearchQuery = ref('')
  const hubspotCompaniesList = ref<HubspotCompaniesListResponse>()
  const isLoadingHubspot = ref(false)

  const openHubspotModal = () => {
    showHubspotModal.value = true
    hubspotSearchQuery.value = ''
    loadHubspotCompanies()
  }

  const loadHubspotCompanies = async () => {
    isLoadingHubspot.value = true
    hubspotCompaniesList.value = await HubspotService.hubspotCompaniesList({
      search: hubspotSearchQuery.value,
      limit: 20
    })
    isLoadingHubspot.value = false
  }

  // Debounced search for HubSpot companies
  let hubspotSearchTimeout: ReturnType<typeof setTimeout> | null = null
  watch(hubspotSearchQuery, (newVal) => {
    if (hubspotSearchTimeout) {
      clearTimeout(hubspotSearchTimeout)
    }
    hubspotSearchTimeout = setTimeout(() => {
      loadHubspotCompanies()
    }, 300)
  })

  // Cleanup timeout on component unmount
  onUnmounted(() => {
    if (hubspotSearchTimeout) {
      clearTimeout(hubspotSearchTimeout)
    }
  })

  const isImportingHubspot = ref(false)

  const selectHubspotCompany = async (company: SuperUserHubSpotCompany) => {
    isImportingHubspot.value = true

    // Fetch detailed onboarding data from HubSpot
    const onboardingData = await HubspotService.hubspotCompaniesOnboardingDataRetrieve({
      uuid: company.uuid
    })

    // Import company data into the form
    if (onboardingData.name) {
      formData.name = onboardingData.name
    }
    if (onboardingData.phone) {
      formData.phone = onboardingData.phone
    }
    if (onboardingData.street) {
      formData.street = onboardingData.street
    }
    if (onboardingData.street_number) {
      formData.street_number = onboardingData.street_number
    }
    if (onboardingData.zip) {
      formData.zip = onboardingData.zip
    }
    if (onboardingData.city) {
      formData.city = onboardingData.city
    }
    if (onboardingData.country) {
      formData.country = onboardingData.country
    }
    if (onboardingData.domain) {
      // Use domain to construct email if no email is set
      formData.email = `info@${onboardingData.domain}`
    }

    // Import contacts as users
    if (onboardingData.contacts && onboardingData.contacts.length > 0) {
      // Clear existing users and add the ones from HubSpot
      users.value = onboardingData.contacts.map((contact, index) => ({
        id: userIdCounter++,
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        role: index === 0 ? 'ADMIN' : 'LAWYER' // First contact becomes admin
      }))

      // If any contact has a phone and we don't have company phone, use it
      if (!formData.phone) {
        const contactWithPhone = onboardingData.contacts.find((c) => c.phone)
        if (contactWithPhone?.phone) {
          formData.phone = contactWithPhone.phone
        }
      }
    }

    isImportingHubspot.value = false
    showHubspotModal.value = false
  }

  // Stripe customer creation modal state
  const showStripeModal = ref(false)
  const isCreatingStripe = ref(false)
  const stripeFormData = reactive({
    name: '',
    email: '',
    phone: '',
    street: '',
    street_number: '',
    zip_code: '',
    city: '',
    country: 'Germany'
  })

  const openStripeModal = () => {
    // Pre-fill with data from the main form
    stripeFormData.name = formData.name
    stripeFormData.email = formData.email
    stripeFormData.phone = formData.phone
    stripeFormData.street = formData.street
    stripeFormData.street_number = formData.street_number
    stripeFormData.zip_code = formData.zip
    stripeFormData.city = formData.city
    stripeFormData.country = formData.country
    showStripeModal.value = true
  }

  const createStripeCustomer = async () => {
    // Validate required fields
    const errors: string[] = []

    if (!stripeFormData.name || stripeFormData.name.trim().length < 2) {
      errors.push(t('validation.stripeNameRequired'))
    }

    if (!stripeFormData.email) {
      errors.push(t('validation.stripeEmailRequired'))
    } else {
      const emailRegex = /^.+@.+\.[a-z]{2,}$/i
      if (!emailRegex.test(stripeFormData.email)) {
        errors.push(t('validation.stripeEmailInvalid'))
      }
    }

    if (errors.length > 0) {
      // Show specific validation errors
      toast({
        title: t('errors.validationError'),
        description: t('errors.stripeValidationFailed'),
        variant: 'destructive'
      })
      return
    }

    isCreatingStripe.value = true

    try {
      const newCustomer = await StripeCustomersService.stripeCustomersCreate({
        requestBody: {
          name: stripeFormData.name,
          email: stripeFormData.email,
          phone: stripeFormData.phone || undefined,
          street: stripeFormData.street || undefined,
          street_number: stripeFormData.street_number || undefined,
          city: stripeFormData.city || undefined,
          zip_code: stripeFormData.zip_code || undefined,
          country: stripeFormData.country || undefined
        }
      })

      // Add the new customer to the list and select it
      stripeCustomersList.value?.results.unshift(newCustomer)
      formData.stripe_customer_uuid = newCustomer.uuid

      createSuccessToast()
      showStripeModal.value = false
    } catch (error) {
      toast({
        title: t('errors.validationError'),
        description: t('errors.stripeCustomerCreationFailed'),
        variant: 'destructive'
      })
    } finally {
      isCreatingStripe.value = false
    }
  }

  const isProcessing = ref(false)

  const handleSubmit = async (values: GenericObject) => {
    // Validate users
    if (users.value.length === 0) {
      toast({
        title: t('errors.validationError'),
        description: t('errors.atLeastOneUserRequired'),
        variant: 'destructive'
      })
      return
    }

    // Validate all users
    if (!validateAllUsers()) {
      toast({
        title: t('errors.validationError'),
        description: t('errors.fixUserInformationErrors'),
        variant: 'destructive'
      })
      return
    }

    // Check for duplicate email addresses
    const emailSet = new Set<string>()
    for (const user of users.value) {
      const email = user.email.toLowerCase().trim()
      if (emailSet.has(email)) {
        toast({
          title: t('errors.validationError'),
          description: t('errors.duplicateEmailFound'),
          variant: 'destructive'
        })
        return
      }
      emailSet.add(email)
    }

    isProcessing.value = true

    // Convert onboarding_start_date from YYYY-MM-DD and time HH:MM to ISO format if set
    let onboardingDate: string | null = null
    if (formData.onboarding_start_date) {
      const dateValue = formData.onboarding_start_date
      let year, month, day

      // Handle both formats: YYYY-MM-DD (from date input) or DD.MM.YYYY (legacy)
      if (dateValue.includes('-')) {
        // YYYY-MM-DD format
        ;[year, month, day] = dateValue.split('-').map(Number)
      } else if (dateValue.includes('.')) {
        // DD.MM.YYYY format (legacy)
        ;[day, month, year] = dateValue.split('.').map(Number)
      } else {
        // Try parsing as ISO date
        const parsed = new Date(dateValue)
        year = parsed.getFullYear()
        month = parsed.getMonth() + 1
        day = parsed.getDate()
      }

      const [hours, minutes] = formData.onboarding_start_time.split(':')
      onboardingDate = new Date(year, month - 1, day, Number(hours), Number(minutes)).toISOString()
    }

    const requestBody = {
      name: formData.name,
      street: formData.street,
      street_number: formData.street_number,
      zip: formData.zip,
      city: formData.city,
      country: formData.country,
      phone: formData.phone,
      email: formData.email,
      seats: formData.seats,
      appointments_seats: formData.appointments_seats,
      law_fields: formData.law_fields,
      has_churned: formData.has_churned,
      monsum_customer_number: formData.monsum_customer_number || undefined,
      is_test: formData.is_test,
      inbound_email_slug: formData.inbound_email_slug || undefined,
      enable_inbound_email_replies: formData.enable_inbound_email_replies,
      can_configure_email: formData.can_configure_email,
      is_new_ui_switch_enabled: formData.is_new_ui_switch_enabled,
      is_forced_to_use_new_ui: formData.is_forced_to_use_new_ui,
      onboarding_start_date: onboardingDate,
      enable_cases: formData.enable_cases,
      enable_voice: formData.enable_voice,
      enable_law_specific_data: formData.enable_law_specific_data,
      ai_enabled: formData.ai_enabled,
      enable_appointments: formData.enable_appointments,
      enable_ai_workflows: formData.enable_ai_workflows,
      default_language: formData.default_language,
      legal_jurisdiction: formData.legal_jurisdiction,
      stripe_customer_uuid: formData.stripe_customer_uuid ?? undefined,
      users: users.value.map((u) => ({
        email: u.email,
        first_name: u.first_name,
        last_name: u.last_name,
        role: u.role
      }))
    }

    try {
      const response = await OrganisationsService.organisationsCreate({
        requestBody: requestBody as any
      })
      createSuccessToast()
      router.push(ROUTES.ADMIN.CUSTOMERS.CUSTOMER_DETAIL.replace(':uuid', response.uuid))
    } catch (error) {
      toast({
        title: t('errors.validationError'),
        description: t('errors.customerCreationFailed'),
        variant: 'destructive'
      })
    } finally {
      isProcessing.value = false
    }
  }

  const goBack = () => {
    router.push(ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST)
  }

  onMounted(async () => {
    stripeCustomersList.value = await StripeCustomersService.stripeCustomersList({ limit: 0 })
    lawFields.value = await LawFieldsService.lawFieldsRetrieve()
  })
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST },
          { title: 'New Customer', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_CREATE }
        ]"
      />
    </div>

    <router-link
      :to="ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST"
      class="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700"
    >
      <ArrowLeft class="h-4 w-4" />
      {{ t('backToList') }}
    </router-link>

    <h1 class="text-2xl font-bold text-gray-900">{{ t('newCustomer') }}</h1>

    <Form
      v-slot="{ errors }"
      :validation-schema="validationSchema"
      @submit="handleSubmit"
    >
      <!-- Customer Information Section -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-semibold text-gray-800">{{ t('customerInformation') }}</h2>
            <Pencil class="h-4 w-4 text-gray-400" />
          </div>
          <button
            type="button"
            data-cy="hubspot-import-button"
            class="inline-flex items-center gap-2 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-sm
              font-medium text-orange-700 transition-colors hover:bg-orange-100 dark:border-orange-600
              dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/40"
            @click="openHubspotModal"
          >
            <Building2 class="h-4 w-4" />
            {{ t('importFromHubspot') }}
          </button>
        </div>

        <FormGrid>
          <div class="col-span-6">
            <InputField
              v-model="formData.name"
              :label="t('customerName')"
              name="name"
              :asterisk="true"
              :disabled="isProcessing"
              data-cy="customer-name-input"
            />
          </div>
          <div class="col-span-6">
            <InputField
              v-model="formData.primary_contact"
              :label="t('primaryContact')"
              name="primary_contact"
              :disabled="isProcessing"
            />
          </div>
        </FormGrid>

        <FormGrid>
          <div class="col-span-6">
            <InputField
              v-model="formData.phone"
              :label="t('phone')"
              name="phone"
              type="tel"
              :show-icon="true"
              :disabled="isProcessing"
              data-cy="customer-phone-input"
            />
          </div>
          <div class="col-span-6">
            <InputField
              v-model="formData.email"
              :label="t('email')"
              name="email"
              type="email"
              :asterisk="true"
              :show-icon="true"
              :disabled="isProcessing"
              data-cy="customer-email-input"
            />
          </div>
        </FormGrid>

        <FormGrid>
          <div class="col-span-9">
            <InputField
              v-model="formData.street"
              :label="t('street')"
              name="street"
              :asterisk="true"
              :disabled="isProcessing"
              data-cy="customer-street-input"
            />
          </div>
          <div class="col-span-3">
            <InputField
              v-model="formData.street_number"
              :label="t('house')"
              name="street_number"
              :asterisk="true"
              :disabled="isProcessing"
              data-cy="customer-street-number-input"
            />
          </div>
        </FormGrid>

        <FormGrid>
          <div class="col-span-3">
            <InputField
              v-model="formData.zip"
              :label="t('postcode')"
              name="zip"
              :asterisk="true"
              :disabled="isProcessing"
              data-cy="customer-zip-input"
            />
          </div>
          <div class="col-span-5">
            <InputField
              v-model="formData.city"
              :label="t('city')"
              name="city"
              :asterisk="true"
              :disabled="isProcessing"
              data-cy="customer-city-input"
            />
          </div>
          <div class="col-span-4">
            <InputField
              v-model="formData.country"
              :label="t('country')"
              name="country"
              :asterisk="true"
              :disabled="isProcessing"
              data-cy="customer-country-input"
            />
          </div>
        </FormGrid>
      </div>

      <!-- Billing Information Section -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-semibold text-gray-800">{{ t('billingInformation') }}</h2>
            <Pencil class="h-4 w-4 text-gray-400" />
          </div>
          <button
            type="button"
            data-cy="create-stripe-customer-button"
            class="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm
              font-medium text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-600
              dark:bg-indigo-900/20 dark:text-indigo-400 dark:hover:bg-indigo-900/40"
            @click="openStripeModal"
          >
            <CreditCard class="h-4 w-4" />
            {{ t('createStripeCustomer') }}
          </button>
        </div>

        <p class="mb-4 text-sm text-gray-600">{{ t('connectToStripe') }} {{ t('orCreateNew') }}</p>

        <SelectField
          v-model="formData.stripe_customer_uuid"
          :label="t('stripeCustomerId')"
          name="stripe_customer_uuid"
          :is-filterable="true"
          :options="stripeCustomersOptions"
          :disabled="isProcessing"
        />
      </div>

      <!-- Assign Users Section -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex items-center gap-2">
          <h2 class="text-lg font-semibold text-gray-800">{{ t('assignUsers') }}</h2>
          <Pencil class="h-4 w-4 text-gray-400" />
        </div>

        <!-- Users List -->
        <div
          v-if="users.length > 0"
          class="mb-4 space-y-3"
        >
          <div
            v-for="user in users"
            :key="user.id"
            class="flex flex-col gap-2"
          >
            <div
              data-cy="user-row"
              class="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-neutral-600
                dark:bg-neutral-700"
            >
              <div class="grid flex-1 grid-cols-12 gap-3">
                <div class="col-span-2">
                  <input
                    v-model="user.first_name"
                    type="text"
                    :placeholder="t('firstName') + ' *'"
                    :class="[
                      'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1',
                      userValidationErrors[user.id]?.first_name
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500'
                    ]"
                    class="dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                    @blur="validateUser(user)"
                  />
                </div>
                <div class="col-span-2">
                  <input
                    v-model="user.last_name"
                    type="text"
                    :placeholder="t('lastName') + ' *'"
                    :class="[
                      'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1',
                      userValidationErrors[user.id]?.last_name
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500'
                    ]"
                    class="dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                    @blur="validateUser(user)"
                  />
                </div>
                <div class="col-span-5">
                  <input
                    v-model="user.email"
                    type="email"
                    :placeholder="t('email') + ' *'"
                    :class="[
                      'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1',
                      userValidationErrors[user.id]?.email
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500'
                    ]"
                    class="dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                    @blur="validateUser(user)"
                  />
                </div>
                <select
                  v-model="user.role"
                  class="col-span-2 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
                    focus:outline-none focus:ring-1 focus:ring-purple-500 dark:border-neutral-600 dark:bg-neutral-800
                    dark:text-white"
                >
                  <option
                    v-for="option in roleOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.title }}
                  </option>
                </select>
                <div class="col-span-1 flex items-center justify-center">
                  <button
                    type="button"
                    class="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                    @click="removeUser(user.id)"
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            <!-- Validation error messages -->
            <div
              v-if="userValidationErrors[user.id]"
              class="grid grid-cols-12 gap-3 px-3"
            >
              <p
                v-if="userValidationErrors[user.id].first_name"
                class="col-span-2 text-xs text-red-600"
              >
                {{ userValidationErrors[user.id].first_name }}
              </p>
              <p
                v-if="userValidationErrors[user.id].last_name"
                class="col-span-2 text-xs text-red-600"
              >
                {{ userValidationErrors[user.id].last_name }}
              </p>
              <p
                v-if="userValidationErrors[user.id].email"
                class="col-span-5 text-xs text-red-600"
              >
                {{ userValidationErrors[user.id].email }}
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          data-cy="add-user-button"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm
            font-medium text-gray-700 hover:bg-gray-50"
          :disabled="isProcessing"
          @click="addUser"
        >
          <Plus class="h-4 w-4" />
          {{ t('addUser') }}
        </button>
      </div>

      <!-- Selected Onboarding Time Section -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex items-center gap-2">
          <h2 class="text-lg font-semibold text-gray-800">{{ t('selectedOnboardingTime') }}</h2>
          <Pencil class="h-4 w-4 text-gray-400" />
        </div>

        <div
          class="cursor-pointer rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-700 transition-colors
            hover:border-purple-500"
          :class="[isProcessing ? 'cursor-not-allowed opacity-50' : '']"
          @click="!isProcessing && (showOnboardingModal = true)"
        >
          <span
            v-if="onboardingDisplay"
            class="text-sm"
          >
            {{ onboardingDisplay }}
          </span>
          <span
            v-else
            class="text-sm text-gray-400"
          >
            {{ t('selectOnboardingTime') }}
          </span>
        </div>

        <!-- Hidden inputs to maintain form data -->
        <input
          v-model="formData.onboarding_start_date"
          type="hidden"
        />
        <input
          v-model="formData.onboarding_start_time"
          type="hidden"
        />
      </div>

      <!-- Account Settings Section -->
      <div
        class="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="mb-4 flex items-center gap-2">
          <h2 class="text-lg font-semibold text-gray-800">{{ t('accountSettings') }}</h2>
          <Pencil class="h-4 w-4 text-gray-400" />
        </div>

        <MultiSelectField
          v-model="formData.law_fields"
          :label="t('lawFields')"
          name="law_fields"
          :options="lawFieldOptions"
          :is-filterable="true"
          :disabled="isProcessing"
        />

        <FormGrid>
          <div class="col-span-6">
            <InputField
              v-model="formData.seats"
              :label="t('seats')"
              name="seats"
              type="number"
              :asterisk="true"
              :show-icon="true"
              :disabled="isProcessing"
              data-cy="customer-seats-input"
            />
          </div>
          <div class="col-span-6">
            <InputField
              v-model="formData.appointments_seats"
              :label="t('appointmentBookingSeats')"
              name="appointments_seats"
              type="number"
              :asterisk="true"
              :show-icon="true"
              :disabled="isProcessing"
              data-cy="customer-appointments-seats-input"
            />
          </div>
        </FormGrid>

        <InputField
          v-model="formData.monsum_customer_number"
          :label="t('monsumCustomerNumber')"
          name="monsum_customer_number"
          :disabled="isProcessing"
        />

        <CheckboxField
          v-model="formData.has_churned"
          :label="t('hasChurned')"
          name="has_churned"
          :disabled="isProcessing"
        />

        <!-- Features Section -->
        <div class="mt-6">
          <h3 class="mb-3 text-base font-semibold text-gray-800">{{ t('features') }}</h3>

          <div class="space-y-2">
            <CheckboxField
              v-model="documentAiEnabled"
              :label="t('activateAI')"
              name="document_ai"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.enable_cases"
              :label="t('activateCases')"
              name="enable_cases"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.enable_voice"
              :label="t('activateAIAnsweringMachine')"
              name="enable_voice"
              :disabled="isProcessing"
            />
            <SelectField
              v-model="formData.default_language"
              :label="t('phonebotLanguage')"
              name="default_language"
              :options="languageOptions"
              :disabled="isProcessing"
            />
            <SelectField
              v-model="formData.legal_jurisdiction"
              :label="t('legalJurisdiction')"
              name="legal_jurisdiction"
              :options="jurisdictionOptions"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.enable_inbound_email_replies"
              :label="t('emailResponseFromClients')"
              name="enable_inbound_email_replies"
              :disabled="isProcessing"
            />
            <InputField
              v-model="formData.inbound_email_slug"
              :label="t('inboundEmailSlug')"
              name="inbound_email_slug"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.enable_appointments"
              :label="t('appointmentBooking')"
              name="enable_appointments"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.enable_ai_workflows"
              :label="t('aiWorkflows')"
              name="enable_ai_workflows"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.is_new_ui_switch_enabled"
              :label="t('enableNewUiSwitch')"
              name="is_new_ui_switch_enabled"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.is_forced_to_use_new_ui"
              :label="t('forceUseOfNewUi')"
              name="is_forced_to_use_new_ui"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.is_test"
              :label="t('isTestAccount')"
              name="is_test"
              :disabled="isProcessing"
            />
            <CheckboxField
              v-model="formData.can_configure_email"
              :label="t('canConfigureEmail')"
              name="can_configure_email"
              :disabled="isProcessing"
            />
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div
        class="mt-6 flex items-center justify-between gap-4 border-t border-gray-200 bg-white px-6 py-4"
      >
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm
            font-medium text-gray-700 hover:bg-gray-50"
          :disabled="isProcessing"
          @click="goBack"
        >
          <ArrowLeft class="h-4 w-4" />
          {{ t('back') }}
        </button>
        <button
          type="submit"
          data-cy="submit-create-customer-button"
          class="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-semibold text-white
            hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500
            disabled:pointer-events-none disabled:opacity-50"
          :disabled="Object.keys(errors).length > 0 || isProcessing"
        >
          {{ t('finishAndSendInvites') }}
        </button>
      </div>
    </Form>

    <!-- HubSpot Import Modal -->
    <Dialog v-model:open="showHubspotModal">
      <DialogContent class="max-w-2xl">
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2">
            <Building2 class="h-5 w-5 text-orange-500" />
            {{ t('hubspotImportTitle') }}
          </DialogTitle>
          <DialogDescription>
            {{ t('hubspotImportDescription') }}
          </DialogDescription>
        </DialogHeader>

        <!-- Search Input -->
        <div class="relative">
          <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search class="h-4 w-4 text-gray-400" />
          </div>
          <input
            v-model="hubspotSearchQuery"
            type="text"
            :placeholder="t('searchHubspotCompanies')"
            class="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm
              placeholder-gray-400 focus:border-orange-500 focus:outline-none focus:ring-2
              focus:ring-orange-500/20 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white
              dark:placeholder-neutral-400"
          />
        </div>

        <!-- Results List -->
        <div
          class="max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-neutral-700"
        >
          <div
            v-if="isLoadingHubspot"
            class="flex items-center justify-center py-8"
          >
            <div class="flex items-center gap-2 text-sm text-gray-500">
              <div
                class="h-4 w-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent"
              ></div>
              {{ t('loadingHubspot') }}
            </div>
          </div>

          <div
            v-else-if="!hubspotCompaniesList?.results?.length"
            class="flex items-center justify-center py-8"
          >
            <p class="text-sm text-gray-500">{{ t('noHubspotResults') }}</p>
          </div>

          <ul
            v-else
            class="divide-y divide-gray-200 dark:divide-neutral-700"
          >
            <li
              v-for="company in hubspotCompaniesList.results"
              :key="company.uuid"
              class="group flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-neutral-800"
            >
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <span class="truncate font-medium text-gray-900 dark:text-white">
                    {{ company.name || 'Unnamed Company' }}
                  </span>
                  <a
                    v-if="company.hubspot_url"
                    :href="company.hubspot_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-gray-400 hover:text-orange-500"
                    @click.stop
                  >
                    <ExternalLink class="h-3.5 w-3.5" />
                  </a>
                </div>
                <div
                  class="mt-1 flex items-center gap-4 text-xs text-gray-500 dark:text-neutral-400"
                >
                  <span
                    v-if="company.hs_object_id"
                    class="flex items-center gap-1"
                  >
                    <span class="font-medium">{{ t('hubspotId') }}:</span>
                    {{ company.hs_object_id }}
                  </span>
                  <span
                    v-if="company.domain"
                    class="flex items-center gap-1"
                  >
                    <span class="font-medium">{{ t('domain') }}:</span>
                    {{ company.domain }}
                  </span>
                </div>
              </div>
              <button
                type="button"
                class="ml-4 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-medium
                  text-orange-700 opacity-0 transition-all hover:bg-orange-100 disabled:cursor-not-allowed
                  disabled:opacity-50 group-hover:opacity-100 dark:border-orange-600 dark:bg-orange-900/20
                  dark:text-orange-400 dark:hover:bg-orange-900/40"
                :disabled="isImportingHubspot"
                @click="selectHubspotCompany(company)"
              >
                <span
                  v-if="isImportingHubspot"
                  class="flex items-center gap-1"
                >
                  <div
                    class="h-3 w-3 animate-spin rounded-full border-2 border-orange-500 border-t-transparent"
                  ></div>
                </span>
                <span v-else>{{ t('selectCompany') }}</span>
              </button>
            </li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>

    <!-- Stripe Customer Creation Modal -->
    <Dialog v-model:open="showStripeModal">
      <DialogContent class="max-w-lg">
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2">
            <CreditCard class="h-5 w-5 text-indigo-500" />
            {{ t('stripeCreateTitle') }}
          </DialogTitle>
          <DialogDescription>
            {{ t('stripeCreateDescription') }}
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4">
          <!-- Name -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ t('customerName') }} *
            </label>
            <input
              v-model="stripeFormData.name"
              type="text"
              class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
              :placeholder="t('customerName')"
            />
          </div>

          <!-- Email -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ t('email') }} *
            </label>
            <input
              v-model="stripeFormData.email"
              type="email"
              class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
              :placeholder="t('email')"
            />
          </div>

          <!-- Phone -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ t('phone') }}
            </label>
            <input
              v-model="stripeFormData.phone"
              type="tel"
              class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
              :placeholder="t('phone')"
            />
          </div>

          <!-- Street and House Number -->
          <div class="grid grid-cols-3 gap-3">
            <div class="col-span-2">
              <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ t('street') }}
              </label>
              <input
                v-model="stripeFormData.street"
                type="text"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                  focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                  dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                :placeholder="t('street')"
              />
            </div>
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ t('house') }}
              </label>
              <input
                v-model="stripeFormData.street_number"
                type="text"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                  focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                  dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                :placeholder="t('house')"
              />
            </div>
          </div>

          <!-- Zip and City -->
          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ t('postcode') }}
              </label>
              <input
                v-model="stripeFormData.zip_code"
                type="text"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                  focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                  dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                :placeholder="t('postcode')"
              />
            </div>
            <div class="col-span-2">
              <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                {{ t('city') }}
              </label>
              <input
                v-model="stripeFormData.city"
                type="text"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                  focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                  dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                :placeholder="t('city')"
              />
            </div>
          </div>

          <!-- Country -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              {{ t('country') }}
            </label>
            <input
              v-model="stripeFormData.country"
              type="text"
              class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400
                focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20
                dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
              :placeholder="t('country')"
            />
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end pt-4">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-lg border border-transparent bg-indigo-600 px-4 py-2 text-sm
                font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2
                focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isCreatingStripe || !stripeFormData.name || !stripeFormData.email"
              @click="createStripeCustomer"
            >
              <div
                v-if="isCreatingStripe"
                class="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
              ></div>
              <CreditCard
                v-else
                class="h-4 w-4"
              />
              {{ isCreatingStripe ? t('creating') : t('createCustomer') }}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <!-- Onboarding Time Modal -->
    <Dialog
      :open="showOnboardingModal"
      @update:open="showOnboardingModal = $event"
    >
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{{ t('editOnboardingTime') }}</DialogTitle>
        </DialogHeader>
        <div class="space-y-4 py-4">
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              {{ t('onboardingDate') }}
            </label>
            <input
              v-model="formData.onboarding_start_date"
              type="date"
              class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500
                focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>
          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">
              {{ t('onboardingTime') }}
            </label>
            <input
              v-model="formData.onboarding_start_time"
              type="time"
              class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-purple-500
                focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>
        </div>
        <div class="flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700
              hover:bg-gray-50"
            @click="showOnboardingModal = false"
          >
            {{ t('cancel') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
            @click="showOnboardingModal = false"
          >
            {{ t('save') }}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
